import { useEffect, useMemo, useState } from "react";
import { PdfPreview } from "./PdfPreview";
import { PdfFullscreenModal } from "./PdfFullscreenModal";

interface Props {
  /**
   * The signed lease PDF as base64. We wrap it in a Blob URL so the existing
   * PdfPreview (which takes a URL) can render it without extra plumbing.
   */
  signedPdfBase64: string;
  /** Called when the user confirms and wants to proceed to payment. */
  onContinue: () => void;
  /** Called when the user wants to go back and edit the form. */
  onBack: () => void;
}

function base64ToBlobUrl(base64: string): string {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

export function ReviewStep({ signedPdfBase64, onContinue, onBack }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Build the blob URL once per PDF payload and revoke on unmount so we don't
  // leak the whole signed PDF in memory if the user bounces around steps.
  const blobUrl = useMemo(
    () => base64ToBlobUrl(signedPdfBase64),
    [signedPdfBase64],
  );
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          Review your signed lease
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          This is the final lease with your signature and details. Please read
          through it carefully before continuing to payment — nothing has been
          charged yet.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
          <div className="text-sm font-medium text-slate-800">
            Signed lease preview
          </div>
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            View full screen
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-3 sm:p-4">
          <PdfPreview url={blobUrl} />
        </div>
      </div>

      <PdfFullscreenModal
        url={blobUrl}
        open={fullscreen}
        onClose={() => setFullscreen(false)}
      />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
          />
          <span>
            I have reviewed the signed lease above and everything looks correct.
          </span>
        </label>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3">
        <button
          type="button"
          onClick={onBack}
          className="sm:w-1/3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-700 font-medium hover:bg-slate-50 transition"
        >
          Back to edit
        </button>
        <button
          type="button"
          disabled={!confirmed}
          onClick={onContinue}
          className="flex-1 rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-600 transition"
        >
          Continue to payment
        </button>
      </div>
    </div>
  );
}
